import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials } from '@/mocks/testimonials';

const aboutSEO = {
  title: '关于我们 — 娜娜轻量版',
  description: '了解娜娜轻量版品牌故事、团队理念与用户口碑。娜娜轻量版致力于为追求极简高效的用户打造最优质的轻量化解决方案。',
  keywords: '娜娜轻量版,关于我们,品牌介绍,用户评价,联系方式,轻量版应用',
};

const breadcrumbItems = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

const aboutJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于娜娜轻量版',
    description: '了解娜娜轻量版品牌故事、团队理念与用户口碑。',
    url: 'https://www.nanalite.com/about',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.label,
      item: item.href ? `https://www.nanalite.com${item.href}` : undefined,
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '娜娜轻量版是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '娜娜轻量版是一款专为追求极简高效体验用户打造的轻量化应用，体积小、启动快、功能精准，支持 Windows、macOS、iOS 和 Android 全平台。',
        },
      },
      {
        '@type': 'Question',
        name: '娜娜轻量版是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，娜娜轻量版的核心功能永久免费使用，无广告、无捆绑。我们通过增值服务实现可持续发展。',
        },
      },
      {
        '@type': 'Question',
        name: '娜娜轻量版与完整版有什么区别？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '轻量版精选了最高频使用的40个核心功能，安装包仅28MB，启动速度快2倍。完整版包含120+功能模块，适合有高级需求的用户。两版数据互通，可随时切换。',
        },
      },
      {
        '@type': 'Question',
        name: '娜娜轻量版的数据安全吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们采用AES-256端到端加密，已通过ISO 27001认证，符合GDPR和《个人信息保护法》要求。加密密钥仅存储在用户设备上。',
        },
      },
    ],
  },
];

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title={aboutSEO.title}
        description={aboutSEO.description}
        keywords={aboutSEO.keywords}
        canonicalUrl="https://www.nanalite.com/about"
        ogTitle="关于我们 — 娜娜轻量版"
        ogDescription="了解娜娜轻量版品牌故事、团队理念与用户口碑。"
        jsonLd={aboutJsonLd}
      />
      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Brand Introduction */}
          <section className="py-10 md:py-16">
            <div className="max-w-3xl">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 mb-6">
                关于<span className="text-primary-500">娜娜轻量版</span>
              </h1>
              <p className="text-foreground-600 leading-relaxed mb-4">
                娜娜轻量版诞生于一个简单的观察：当今的应用越来越臃肿，但用户真正需要的功能其实并不多。我们相信，一款好的工具应该像空气一样轻盈——你察觉不到它的存在，却离不开它。
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                2024年，一支由资深产品设计师和工程师组成的团队走到了一起。我们都有一个共同的信念：科技应该服务于人，而不是让人疲于应付。于是，娜娜轻量版应运而生。
              </p>
              <p className="text-foreground-600 leading-relaxed">
                我们坚持"少即是多"的设计哲学，用数据驱动功能取舍，只保留那些真正被高频使用的模块。每一行代码、每一个像素，都经过精心打磨，只为给你带来最纯粹的使用体验。
              </p>
            </div>

            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-eye-line', label: '使命', value: '让每个人都能轻松高效地工作与生活' },
                { icon: 'ri-heart-line', label: '理念', value: '少即是多，精准胜于全面' },
                { icon: 'ri-star-line', label: '目标', value: '成为1亿用户的首选效率工具' },
                { icon: 'ri-shield-star-line', label: '承诺', value: '用户数据安全永远第一' },
              ].map((item, index) => (
                <div
                  key={index}
                  className="p-6 rounded-2xl bg-background-100 border border-background-200/70 text-center"
                >
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">{item.label}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{item.value}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Timeline */}
          <section className="py-12 md:py-16 border-t border-background-200/70">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 text-center mb-10">
              发展<span className="text-accent-500">历程</span>
            </h2>
            <div className="max-w-2xl mx-auto">
              {[
                { year: '2024.03', title: '团队成立', desc: '一群有共同理想的产品人走到一起，开始构想娜娜轻量版。' },
                { year: '2024.09', title: '内测发布', desc: '经过6个月研发，娜娜轻量版开启内测，首批1000名用户参与体验。' },
                { year: '2025.01', title: '正式上线', desc: '娜娜轻量版 v1.0 正式发布，首月用户突破50万。' },
                { year: '2025.06', title: '用户破百万', desc: '凭借口碑传播，注册用户突破100万，评分稳定在4.9分。' },
                { year: '2026.01', title: '全平台覆盖', desc: '完成 Windows、macOS、iOS、Android 四大平台全覆盖。' },
                { year: '2026.08', title: 'v2.0 发布', desc: '娜娜轻量版 v2.0 发布，全球用户突破500万，开启全新篇章。' },
              ].map((item, index) => (
                <div key={index} className="flex gap-4 pb-8">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-accent-500 mt-1.5 shrink-0"></div>
                    {index < 5 && <div className="w-px flex-1 bg-background-200 mt-2"></div>}
                  </div>
                  <div className="pb-2">
                    <span className="text-xs font-medium text-accent-600">{item.year}</span>
                    <h3 className="text-base font-semibold text-foreground-950 mt-1">{item.title}</h3>
                    <p className="text-sm text-foreground-600 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-12 md:py-16 border-t border-background-200/70">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 text-center mb-4">
              用户<span className="text-primary-500">口碑</span>
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">听听真实用户怎么说</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t, index) => (
                <div
                  key={index}
                  className="p-6 rounded-2xl bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-xs text-amber-400"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-5">"{t.content}"</p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role} · {t.company}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="py-12 md:py-16 border-t border-background-200/70">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 text-center mb-10">
              常见<span className="text-secondary-500">问题</span>
            </h2>
            <div className="max-w-2xl mx-auto space-y-4">
              {[
                {
                  q: '娜娜轻量版是什么？',
                  a: '娜娜轻量版是一款专为追求极简高效体验用户打造的轻量化应用。它体积小（仅28MB）、启动快（0.8秒）、功能精准（只保留核心高频功能），支持 Windows、macOS、iOS 和 Android 全平台。',
                },
                {
                  q: '娜娜轻量版是免费的吗？',
                  a: '是的，娜娜轻量版的核心功能永久免费使用，无广告、无捆绑软件。我们通过企业版增值服务和高级功能订阅来实现可持续发展。',
                },
                {
                  q: '娜娜轻量版与完整版有什么区别？',
                  a: '轻量版精选了使用频率最高的40个核心功能模块，安装包仅为完整版的30%，启动速度快2倍，内存占用低60%。完整版包含120+功能模块，适合有高级需求的用户。两版数据完全互通，可随时切换。',
                },
                {
                  q: '娜娜轻量版的数据安全吗？',
                  a: '我们采用AES-256端到端加密技术，数据在传输和存储过程中全程加密。已通过ISO 27001信息安全认证，符合GDPR和《个人信息保护法》的严格要求。加密密钥仅存储在用户设备上，我们无法访问你的私人数据。',
                },
                {
                  q: '支持哪些平台？',
                  a: '娜娜轻量版目前支持 Windows 10+、macOS 12+、iOS 15+ 和 Android 10+ 四大平台。所有平台的数据通过云端实时同步，确保你在任何设备上都能无缝衔接工作。',
                },
                {
                  q: '如何从完整版迁移到轻量版？',
                  a: '首次启动轻量版时，选择"从完整版导入数据"选项，系统会自动同步你的所有数据。你也可以通过设置中的"导入/导出"功能随时进行数据迁移。',
                },
              ].map((faq, index) => (
                <FAQItem key={index} question={faq.q} answer={faq.a} />
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="py-12 md:py-16 border-t border-background-200/70">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 text-center mb-10">
              联系<span className="text-primary-500">我们</span>
            </h2>
            <div className="max-w-2xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
                {[
                  { icon: 'ri-mail-line', title: '邮箱', value: 'contact@nanalite.com' },
                  { icon: 'ri-phone-line', title: '电话', value: '400-888-9999' },
                  { icon: 'ri-map-pin-line', title: '地址', value: '北京市朝阳区科技园区创新大厦12层' },
                ].map((item, index) => (
                  <div key={index} className="p-5 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 mb-3">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">{item.title}</h3>
                    <p className="text-xs text-foreground-600">{item.value}</p>
                  </div>
                ))}
              </div>

              <ContactForm />
            </div>
          </section>
        </div>
      </main>
    </>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-2xl bg-background-100 border border-background-200/70 overflow-hidden">
      <button
        className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <h3 className="text-sm font-semibold text-foreground-950 pr-4">{question}</h3>
        <i
          className={`text-lg text-foreground-500 transition-transform duration-200 ${
            open ? 'ri-subtract-line' : 'ri-add-line'
          }`}
        ></i>
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-sm text-foreground-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

async function submitContactForm(e: React.FormEvent<HTMLFormElement>) {
  e.preventDefault();
  const form = e.currentTarget;
  const formData = new FormData(form);
  const email = formData.get('email') as string;
  const message = formData.get('message') as string;

  if (!email || !message) {
    return;
  }

  try {
    const res = await fetch(form.getAttribute('action') || '', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        email,
        message,
        name: (formData.get('name') as string) || '',
        subject: (formData.get('subject') as string) || '',
      }).toString(),
    });
    const text = await res.text();
    let parsed;
    try { parsed = JSON.parse(text); } catch { parsed = null; }

    const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
    if (res.ok && parsed?.code === 'OK') {
      alert('提交成功！我们会尽快回复您。');
      form.reset();
    } else if (serverMsg.includes('spam')) {
      alert('提交失败，请稍后重试。');
    } else {
      alert('提交失败，请稍后重试。');
    }
  } catch {
    alert('网络错误，请稍后重试。');
  }
}

function ContactForm() {
  return (
    <form
      id="contact-form"
      data-readdy-form=""
      action="https://readdy.ai/api/form/d9sn7058o1vn02ekuiag"
      method="POST"
      onSubmit={submitContactForm}
      className="space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">
            姓名
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
            placeholder="请输入姓名"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">
            邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
            placeholder="请输入邮箱"
          />
        </div>
      </div>
      <div>
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-2">
          主题
        </label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
          placeholder="请输入主题"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">
          留言 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors resize-none"
          placeholder="请输入留言内容（不超过500字）"
        ></textarea>
      </div>
      <input
        type="text"
        name="phone_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="honeypot-field"
      />
      <button
        type="submit"
        className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
      >
        发送留言
        <i className="ri-send-plane-line ml-2"></i>
      </button>
    </form>
  );
}